import InfiniteScroll from '../../../../components/InfiniteScroll/InfiniteScroll';

import { useState } from 'react';
import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { AppDownloadModal } from '../../../../components/AppDownloadModal/AppDownloadModal';
import { AppStoreButtons } from '../../../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { FishCatchesIcon } from '../../../../components/Icons/FishCatchesIcon';
import { SinglePostListItem } from '../../../../components/PostsViewer/components/SinglePostListItem/SinglePostListItem';
import { Text } from '../../../../components/Text/Text';
import { reportMykissClientError } from '@lib/errors/client';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import {
  ProfileUserFields,
  GetUsersPosts,
  GetUsersPostsVariables,
} from '../../../../generated/graphql';
import { getUsersPosts } from '@graphql/queries/rutilus/Users';
import { NotFound } from '../../../NotFound/NotFound';
import { AnglerPostsViewLoader } from './AnglerPostsViewLoader';

interface IProps {
  nickname: string;
  user: ProfileUserFields;
  usersOwnPage: boolean;
  className?: string;
}

export const AnglerPostsView = ({ nickname, usersOwnPage, className }: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const variables: GetUsersPostsVariables = { nickname };
  const { data, error, fetchMore } = useRutilusQuery(getUsersPosts, {
    variables,
  });

  if (error) {
    return <NotFound />;
  }

  if (!data) {
    return <AnglerPostsViewLoader className={className} />;
  }

  const angler = data.user;
  const { hasNextPage, endCursor } = angler.posts.pageInfo;
  const anglerPosts = getNodesFromConnection(angler.posts);
  const emptyPostText = `It looks like ${nickname} hasn't posted anything yet`;

  const fetchNextPage = async (): Promise<unknown> => {
    setIsFetchingMore(true);
    return fetchMore({
      variables: {
        ...variables,
        after: endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }): GetUsersPosts => {
        return {
          ...fetchMoreResult,
          user: {
            ...fetchMoreResult.user,
            posts: {
              ...fetchMoreResult.user.posts,
              edges: [
                ...(prev.user.posts.edges ?? []),
                ...(fetchMoreResult.user.posts.edges ?? []),
              ],
            },
          },
        };
      },
    })
      .then(() => {
        setIsFetchingMore(false);
      })
      .catch(reportMykissClientError);
  };

  return (
    <div className={className} data-testid="AnglerPostsView">
      <InfiniteScroll
        next={fetchNextPage}
        hasMore={hasNextPage}
        loader={<></>}
        dataLength={anglerPosts.length}
        className="bg-sea-foam mb-8 flex size-full flex-col gap-y-4 px-0 pb-4 md:px-0"
      >
        {anglerPosts.map(post => {
          return (
            <SinglePostListItem
              className="border-lake-fog bg-sea-foam rounded-lg border-2"
              key={post._id}
              post={post}
              onNotLoggedIn={() => {
                setIsAppDownloadModalOpen(true);
              }}
            />
          );
        })}
      </InfiniteScroll>
      {isFetchingMore && <AnglerPostsViewLoader />}
      {anglerPosts.length === 0 && (
        <>
          {usersOwnPage ? (
            <div className="text-midnight mb-10 flex flex-col items-center text-center">
              <Text className="w-2/3 italic">Add your first post in the fishbrain app today!</Text>
              <div className="text-twilight">
                <FishCatchesIcon size={100} />
              </div>
              <AppStoreButtons source="AnglerPostsView" />
            </div>
          ) : (
            <div className="mb-8 flex flex-col items-center justify-center text-center">
              <Text className="w-5/6 sm:w-1/2">{emptyPostText}</Text>
              <div className="text-twilight">
                <FishCatchesIcon size={100} />
              </div>
            </div>
          )}
        </>
      )}
      <AppDownloadModal
        appDownloadSource="AnglerPostsView"
        modalHeader={`See all catches and posts from ${nickname} in the Fishbrain app`}
        modalText="Download the app to see more info about your favorite anglers, ability to follow them and see what they catch next"
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
        showTextOnMobile
      />
    </div>
  );
};
